import { ArrowRight, Star, Zap, Shield, Check, ExternalLink } from 'lucide-react';
import type { DesignOption } from '../data/designs';

interface DesignCardProps {
  design: DesignOption;
  index: number;
}

const statusConfig = {
  recommended: {
    badgeClass: 'status-badge recommended',
    cardClass: 'design-card recommended',
    icon: Star,
  },
  base: {
    badgeClass: 'status-badge base',
    cardClass: 'design-card',
    icon: Shield,
  },
  alternative: {
    badgeClass: 'status-badge alternative',
    cardClass: 'design-card',
    icon: Zap,
  },
};

const gradeConfig = {
  A: { class: 'grade-badge a', label: 'Excelente' },
  B: { class: 'grade-badge b', label: 'Bueno' },
  C: { class: 'grade-badge c', label: 'Mejorable' },
};

export default function DesignCard({ design, index }: DesignCardProps) {
  const status = statusConfig[design.status];
  const StatusIcon = status.icon;
  const grade = gradeConfig[design.cwvScores.overall];
  const animationDelay = `animate-in-delay-${index + 1}`;

  return (
    <article className={`${status.cardClass} ${animationDelay}`}>
      {/* Preview with screenshot */}
      <div className="card-preview" style={{ overflow: 'hidden' }}>
        {/* Screenshot image */}
        <img
          src={design.image}
          alt={`Preview: ${design.name}`}
          className="w-full h-full object-cover"
          style={{ objectPosition: 'left 15%' }}
          loading="lazy"
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 right-4 z-10">
          <span className={status.badgeClass}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {design.statusLabel}
          </span>
        </div>

        {/* Recommended Indicator */}
        {design.status === 'recommended' && (
          <div className="absolute top-4 left-4 z-10">
            <div className="flex items-center gap-1 bg-[#C75B2A] px-2 py-1 rounded-full shadow-lg">
              <Star className="w-3 h-3 text-white fill-white" />
              <span className="text-xs font-semibold text-white">Top Pick</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-grow">
        {/* Header */}
        <div className="mb-4">
          <h3 className="font-serif text-xl font-medium text-[#F0F0F0] mb-2">
            {design.displayName}
          </h3>
          <p className="text-sm text-[#666666] leading-relaxed">
            {design.description}
          </p>
        </div>

        {/* CWV Scores */}
        <div className="mb-4 p-4 bg-[#0A0A0A] rounded-lg border border-[rgba(255,255,255,0.07)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-[#666666] uppercase tracking-wider">
              Core Web Vitals
            </span>
            <span className={grade.class} title={grade.label}>
              {design.cwvScores.overall}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 bg-[#141414] rounded">
              <p className="text-xs text-[#666666] mb-1">LCP</p>
              <p className={`text-sm font-semibold ${
                design.cwvScores.overall === 'A' ? 'text-[#3ddc84]' : 
                design.cwvScores.overall === 'B' ? 'text-[#f5a623]' : 'text-[#ff5757]'
              }`}>{design.cwvScores.lcp}</p>
            </div>
            <div className="text-center p-2 bg-[#141414] rounded">
              <p className="text-xs text-[#666666] mb-1">CLS</p>
              <p className={`text-sm font-semibold ${
                design.cwvScores.overall === 'A' ? 'text-[#3ddc84]' : 
                design.cwvScores.overall === 'B' ? 'text-[#f5a623]' : 'text-[#ff5757]'
              }`}>{design.cwvScores.cls}</p>
            </div>
            <div className="text-center p-2 bg-[#141414] rounded">
              <p className="text-xs text-[#666666] mb-1">INP</p>
              <p className={`text-sm font-semibold ${
                design.cwvScores.overall === 'A' ? 'text-[#3ddc84]' : 
                design.cwvScores.overall === 'B' ? 'text-[#f5a623]' : 'text-[#ff5757]'
              }`}>{design.cwvScores.inp}</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {design.features.slice(0, 4).map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#A0A0A0]">
              <Check className="w-4 h-4 text-[#C75B2A] mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <a
          href={design.path}
          className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium transition-all duration-200 mt-auto ${
            design.status === 'recommended'
              ? 'bg-[#C75B2A] text-white hover:bg-[#D46937]'
              : 'bg-[#1C1C1C] text-[#F0F0F0] hover:bg-[#252525] border border-[rgba(255,255,255,0.07)]'
          }`}
        >
          Ver preview
          {design.status === 'recommended' ? (
            <ArrowRight className="w-4 h-4" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
        </a>
      </div>
    </article>
  );
}
